<template>
	<div>
		<CardGrafico
			v-show="!reportMode"
			:title="$t('lossRate')"
			:subtitle="subtitle"
		>
			<template #botoes>
				<ButtonDropdown
					v-if="!reportMode"
					v-model="dropdownDownload"
					style="margin-top: 0.5rem"
					color="trv-neutral"
					:label="$t('download')"
					semantic-class="mini"
					:options="[
						{ label: 'PNG', value: 'image/png' },
						{ label: 'JPEG', value: 'image/jpeg' },
						{ label: 'PDF', value: 'application/pdf' },
						{ label: 'SVG', value: 'image/svg+xml' },
						// { label: '.csv', value: 'text/csv' },
					]"
					outlined
				/>
			</template>
			<template #grafico>
				<Loading v-if="data.length === 0" />
				<div
					v-else
					class="container__graficofazenda"
				>
					<InputSearch
						v-if="!reportMode"
						v-model="textFilter"
						:placeholder="$t('plantationCode')"
					/>
					<Chart
						ref="ref__chart"
						:options="chartOptions"
					/>
					<div class="container__graficofazenda__pagination">
						<Pagination
							v-show="!reportMode"
							ref="pagination"
							:total-records="itemsToBeShowedAndTotal.total"
							:quantity-per-page-options="[10, 15, 20]"
							:quantity-per-page="quantityPerPage"
							@setStartEnd="setStartEnd"
							@setQuantityPerPage="setQuantityPerPage"
						/>
					</div>
				</div>
			</template>
		</CardGrafico>
		<Chart
			v-show="reportMode"
			ref="ref__chart"
			:options="chartOptions"
		/>
	</div>
</template>

<script>
import { Chart } from 'highcharts-vue'
import ButtonDropdown from '../../../../components/ButtonDropdown/ButtonDropdown'
import CardGrafico from '../CardGrafico'
import InputSearch from '../../../../components/InputSearch/InputSearch'
import Loading from '../../../../components/Loading/Loading'
import Pagination from '../../../../components/Pagination/Pagination'

import ControllingPaginationMixins from '../../../../mixins/ControllingPaginationMixins.js'

import { getColorHexByCSSVariable } from '../../../../utils/themesHelper'

export default {
	components: {
		ButtonDropdown,
		CardGrafico,
		Chart,
		InputSearch,
		Loading,
		Pagination,
	},

	/**
	 * Mixins que controla a paginacao do componente
	 */
	mixins: [ControllingPaginationMixins],

	props: {
		reportMode: { type: Boolean, default: false },
		/**
		 * Dados que devem ir para o grafico
		 */
		data: { type: Array, required: true },

		/**
		 * Data do ultimo processamento como subtitulo
		 */
		subtitle: { type: String, required: true },
	},

	data() {
		return {
			dropdownDownload: '',
			chartOptions: {
				/**
				 * Creditos no canto inferior direito do grafico
				 */
				credits: {
					enabled: false,
				},
				/**
				 * Habilita ou desabilita o icone de exportacao
				 */
				exporting: {
					enabled: false,
				},
				/**
				 * Tipo do grafico
				 */
				chart: {
					type: 'column',
				},
				/**
				 * Titulo do grafico
				 */
				title: {
					text: this.reportMode ? this.subtitle : '',
				},
				/**
				 * Balão ao passar o mouse sobre as barras
				 */
				tooltip: {
					formatter: function () {
						return `
            <div class="container__tooltip"> 
              <div class="container__tooltip__title"> ${this.point.category}</div>
              <div class="container__tooltip__info"> 
                <div class="container__tooltip__info__individual">
                  <div>${this.point.name}: </div> 
                  <div>${this.point.y} (%) </div> 
                </div>
              </div>
            </div>
            `
					},
					backgroundColor: '#333647',
					borderRadius: 10,
					borderWidth: 0,
					outside: true,
					shadow: false,
					useHTML: true,
				},
				/**
				 * Legendas do gráfico
				 */
				legend: {
					enabled: false,
				},
				/**
				 * Eixo X
				 */
				xAxis: {
					categories: [],
					labels: {
						style: {
							color: '#01395e',
							fontFamily: 'Open Sans, sans-serif',
							fontSize: '0.8rem',
							fontWeight: 700,
						},
					},
				},
				/**
				 * Eixo Y
				 */
				yAxis: {
					gridLineColor: '#FFFFFF',
					title: {
						text: '',
					},
					labels: {
						enabled: false,
					},
				},
				plotOptions: {
					series: {
						/**
						 * Datalabel, com as porcentagens de cada coluna do grafico
						 */
						dataLabels: {
							crop: false,
							enabled: true,
							style: {
								fontFamily: 'Open Sans, sans-serif',
								fontWeight: 400,
								fontSize: '0.75rem',
							},
							formatter: function () {
								return `${this.y}%`
							},
						},
					},
				},
				/**
				 * Series, onde contem os dados que serao renderizados pelo highcharts
				 */
				series: [],
			},
			textFilter: '',
		}
	},

	computed: {
		/**
		 * Propriedade computada que retorna os itens do grafico que devem ser exibidos
		 */
		itemsToBeShowedAndTotal: function () {
			const itemsFiltred = this.data.filter((f) =>
				f.codigoDoPlantio.toLowerCase().includes(this.textFilter.toLowerCase()),
			)

			return {
				items: itemsFiltred.slice(this.atualStartIndex, this.atualEndIndex),
				total: itemsFiltred.length,
			}
		},
	},

	watch: {
		/**
		 * Atualiza o grafico sempre que a propriedade data e alterada
		 */
		data() {
			// this.setHighchartsOptions();

			return {
				/**
				 * Opção selecionada para o download
				 */
				dropdownDownload: '',
			}
		},

		/**
		 * Seleciona a opcao de download escolhida
		 */
		dropdownDownload(type) {
			if (type) {
				const filename = `${new Date().getTime()}`
				if (type === 'text/csv') {
					const csv = this.$refs.ref__chart.chart.getCSV()
					stringToExportCSV(filename, csv)
				} else {
					this.$refs.ref__chart.chart.exportChart({ filename, type })
				}
				this.dropdownDownload = ''
			}
		},

		/**
		 * Atualiza o grafico sempre que a propriedade data e alterada
		 */
		itemsToBeShowedAndTotal() {
			this.setHighchartsOptions()
		},

		/**
		 * Quando digitar algo no filtro retorna para a primeira pagina
		 */
		textFilter() {
			this.$refs.pagination.resetToFirstPage()
		},
	},

	created() {
		/**
		 * Quantidade de itens por pagina
		 */
		this.quantityPerPage = 15
	},
	methods: {
		/**
		 * Seta os dados do grafico
		 */
		setHighchartsOptions() {
			const colorUtil = getColorHexByCSSVariable('--color-default')
			const colorNaoUtil = getColorHexByCSSVariable('--color-primary')

			const dataChart = { util: [], naoUtil: [], categories: [] }

			this.itemsToBeShowedAndTotal.items.forEach((plantio) => {
				dataChart.util.push({
					name: this.$t('useful'),
					y: plantio.proporcaoArvoreUtil,
					color: colorUtil,
				})

				dataChart.naoUtil.push({
					name: this.$t('notUseful'),
					y: plantio.totalDeArvoresNaoUteis,
					color: colorNaoUtil,
				})

				dataChart.categories.push(plantio.codigoDoPlantio)
			})

			this.chartOptions.series.length = 0
			this.chartOptions.series.push({ data: dataChart.util })
			this.chartOptions.series.push({ data: dataChart.naoUtil })
			this.chartOptions.xAxis.categories = dataChart.categories
		},
	},
}
</script>

<style scoped>
@import url('../../../../css/tooltipSimplesHighcharts.css');
.container__graficofazenda {
	display: grid;
}

.container__graficofazenda__pagination {
	display: flex;
	justify-content: center;
}

@media screen and (min-width: 992px) {
	.container__graficofazenda__pagination {
		justify-content: flex-end;
	}
}
</style>
